import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { DateRange, DateRangePicker } from "ui/components/DateRangePicker";
import { DEFAULT_PRESET, buildRangeFromPreset, resolvePreset } from "ui/components/DateRangePicker/presets";

const STORAGE_KEY = "dashboard-date-range";

interface StoredRange {
  preset: DateRange["preset"];
  fromISO: string | null;
  toISO: string | null;
}

const loadInitialRange = (): DateRange => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return buildRangeFromPreset(DEFAULT_PRESET);
    const parsed: StoredRange = JSON.parse(raw);

    // Relativni preseti se preračunavaju u odnosu na danas (ne pamtimo stare datume).
    if (parsed.preset && parsed.preset !== "custom") {
      return buildRangeFromPreset(parsed.preset);
    }
    // Custom raspon — vrati spremljene datume.
    return {
      preset: "custom",
      from: parsed.fromISO ? new Date(parsed.fromISO) : null,
      to: parsed.toISO ? new Date(parsed.toISO) : null,
    };
  } catch {
    return buildRangeFromPreset(DEFAULT_PRESET);
  }
};

interface DashboardDateRangeContextValue {
  range: DateRange;
  setRange: (range: DateRange) => void;
}

const DashboardDateRangeContext = createContext<DashboardDateRangeContextValue | null>(null);

export const DashboardDateRangeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [range, setRangeState] = useState<DateRange>(loadInitialRange);

  const setRange = useCallback((next: DateRange) => {
    setRangeState(next);
    try {
      const stored: StoredRange = {
        preset: next.preset,
        fromISO: next.preset === "custom" ? next.from?.toISOString() ?? null : null,
        toISO: next.preset === "custom" ? next.to?.toISOString() ?? null : null,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // ignore storage errors
    }
  }, []);

  const value = useMemo(() => ({ range, setRange }), [range, setRange]);

  return (
    <DashboardDateRangeContext.Provider value={value}>
      {children}
    </DashboardDateRangeContext.Provider>
  );
};

/** Vraća trenutni globalni raspon dashboarda + setter. */
export const useDashboardDateRange = (): DashboardDateRangeContextValue => {
  const ctx = useContext(DashboardDateRangeContext);
  if (!ctx) {
    // Fallback ako se hook koristi izvan providera (npr. izolirani render) — default raspon, no-op setter.
    return { range: buildRangeFromPreset(DEFAULT_PRESET), setRange: () => {} };
  }
  return ctx;
};

/** Chip za header dashboarda — koristi globalni raspon iz konteksta. */
export const DashboardDateRangeChip: React.FC = () => {
  const { range, setRange } = useDashboardDateRange();
  return <DateRangePicker value={range} onChange={setRange} align="right" />;
};

// Re-export helpera radi lakšeg importa u karticama.
export { resolvePreset };
